import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { costsApi, stacksApi } from '@/api/client';
import { formatCurrency, VENTURE_LABELS, VENTURES, VentureName, VentureStack, StackStatus } from '@lytmaster/shared';
import { ArrowLeft, Snowflake, Play, ChevronDown, ChevronUp, Info, Database, Server, Cloud, Box, Globe, Shield, HardDrive, Loader2, AlertCircle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import { useState } from 'react';
import { toast } from 'sonner';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const RESOURCE_ICONS: Record<string, typeof Database> = {
  rds: Database,
  aurora: Database,
  ec2: Server,
  ecs: Box,
  lambda: Cloud,
  cloudfront: Globe,
  s3: HardDrive,
  cognito: Shield,
  other: Server,
};

function StatusBadge({ status }: { status: StackStatus }) {
  const styles = {
    running: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    hibernated: 'bg-amber-50 text-amber-700 border-amber-200',
    transitioning: 'bg-blue-50 text-blue-700 border-blue-200',
  };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status]}`}>
      {status === 'transitioning' && <Loader2 className="w-3 h-3 animate-spin" />}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function StackCard({ stack, onHibernate, onResume, isActioning }: {
  stack: VentureStack;
  onHibernate: (stackName: string) => void;
  onResume: (stackName: string) => void;
  isActioning: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showConfirm, setShowConfirm] = useState<'hibernate' | 'resume' | null>(null);

  const hibernatableResources = stack.resources.filter(r => r.canHibernate);
  const shortName = stack.stackName.split('-').slice(-1)[0] || stack.stackName;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-semibold text-slate-900">{stack.stackName}</h4>
            <p className="text-xs text-slate-500 mt-0.5">{shortName} stack</p>
          </div>
          <StatusBadge status={stack.status} />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <div className="text-xs text-slate-500">Cost / Day</div>
            <div className="text-lg font-bold text-slate-900">{formatCurrency(stack.costPerDay)}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Projected / Month</div>
            <div className="text-lg font-bold text-slate-900">{formatCurrency(stack.costPerMonth)}</div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {stack.status === 'running' && hibernatableResources.length > 0 && (
            <button
              onClick={() => setShowConfirm('hibernate')}
              disabled={isActioning}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-sm font-medium hover:bg-amber-100 transition-colors disabled:opacity-50"
            >
              <Snowflake className="w-3.5 h-3.5" />
              Hibernate
            </button>
          )}
          {stack.status === 'hibernated' && (
            <button
              onClick={() => setShowConfirm('resume')}
              disabled={isActioning}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors disabled:opacity-50"
            >
              <Play className="w-3.5 h-3.5" />
              Resume
            </button>
          )}
          {stack.status === 'transitioning' && (
            <span className="flex items-center gap-1.5 text-sm text-blue-600">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              In progress...
            </span>
          )}

          {stack.resources.length > 0 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="ml-auto flex items-center gap-1 px-2 py-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {stack.resources.length} resources
            </button>
          )}
        </div>
      </div>

      {/* Confirmation dialogs */}
      {showConfirm === 'hibernate' && (
        <div className="border-t border-slate-200 bg-amber-50/50 p-4">
          <div className="flex items-start gap-2 mb-3">
            <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-800">
              Hibernation scales billable resources (DB, EC2, ECS) to zero. The stack definition remains intact. No data is lost. You can resume at any time.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { onHibernate(stack.stackName); setShowConfirm(null); }}
              disabled={isActioning}
              className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors disabled:opacity-50"
            >
              {isActioning ? 'Hibernating...' : 'Confirm Hibernate'}
            </button>
            <button
              onClick={() => setShowConfirm(null)}
              className="px-3 py-1.5 bg-white text-slate-600 border border-slate-200 rounded-lg text-sm hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showConfirm === 'resume' && (
        <div className="border-t border-slate-200 bg-emerald-50/50 p-4">
          <div className="flex items-start gap-2 mb-3">
            <Info className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            <p className="text-sm text-emerald-800">
              Resume will restart stopped resources. It may take 2-5 minutes for databases to become available. Applications will be temporarily unavailable during resume.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { onResume(stack.stackName); setShowConfirm(null); }}
              disabled={isActioning}
              className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
            >
              {isActioning ? 'Resuming...' : 'Confirm Resume'}
            </button>
            <button
              onClick={() => setShowConfirm(null)}
              className="px-3 py-1.5 bg-white text-slate-600 border border-slate-200 rounded-lg text-sm hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Expanded resource list */}
      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-4">
          <div className="space-y-2">
            {stack.resources.map((resource) => {
              const Icon = RESOURCE_ICONS[resource.type] || Server;
              const statusColor = resource.status === 'running' ? 'text-emerald-600' :
                resource.status === 'stopped' ? 'text-amber-600' : 'text-blue-600';
              return (
                <div key={resource.id} className="flex items-center gap-3 px-3 py-2 bg-white rounded-lg border border-slate-100">
                  <Icon className="w-4 h-4 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-700 truncate">{resource.name}</div>
                    <div className="text-xs text-slate-400">{resource.type}</div>
                  </div>
                  <div className={`text-xs font-medium ${statusColor}`}>
                    {resource.status}
                  </div>
                  {resource.canHibernate && (
                    <Snowflake className="w-3 h-3 text-slate-300" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function HibernationInfo() {
  const [open, setOpen] = useState(false);

  return (
    <div className="mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
      >
        <Info className="w-4 h-4" />
        What is hibernation?
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="mt-3 p-4 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-600 space-y-2">
          <p><strong>What is hibernation?</strong> Stops billable compute/database resources in this stack. Static resources (S3, CloudFront, DNS) remain active. No data is deleted.</p>
          <p><strong>Cost impact:</strong> While hibernated, you stop paying for the compute/DB portion of this stack.</p>
          <p><strong>Resuming:</strong> Restarts all stopped resources. Takes 2-5 min for databases. Applications will be temporarily unavailable during resume.</p>
        </div>
      )}
    </div>
  );
}

export default function VentureDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const venture = id as VentureName;

  const { data, isLoading, error } = useQuery({
    queryKey: ['venture-costs', venture],
    queryFn: () => costsApi.getVentureCosts(venture, 30, 'DAILY'),
    enabled: VENTURES.includes(venture),
  });

  const { data: stacksData, isLoading: stacksLoading, error: stacksError } = useQuery({
    queryKey: ['venture-stacks', venture],
    queryFn: () => stacksApi.listStacks(venture),
    enabled: VENTURES.includes(venture),
    refetchInterval: (query) => {
      const stacks = query.state.data?.stacks as VentureStack[] | undefined;
      const hasTransitioning = stacks?.some(s => s.status === 'transitioning');
      return hasTransitioning ? 10000 : false;
    },
  });

  const hibernateMutation = useMutation({
    mutationFn: (stackName: string) => stacksApi.hibernate(stackName),
    onMutate: (stackName) => {
      toast.loading(`Hibernating ${stackName}...`, { id: `hibernate-${stackName}` });
    },
    onSuccess: (_data, stackName) => {
      toast.success(`Hibernation initiated for ${stackName}`, {
        id: `hibernate-${stackName}`,
        description: 'Resources are being stopped. This may take a few minutes.',
      });
      queryClient.invalidateQueries({ queryKey: ['venture-stacks', venture] });
    },
    onError: (err: any, stackName) => {
      toast.error(`Failed to hibernate ${stackName}`, {
        id: `hibernate-${stackName}`,
        description: err?.response?.data?.error || err?.message || 'An unexpected error occurred.',
      });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: (stackName: string) => stacksApi.resume(stackName),
    onMutate: (stackName) => {
      toast.loading(`Resuming ${stackName}...`, { id: `resume-${stackName}` });
    },
    onSuccess: (_data, stackName) => {
      toast.success(`Resume initiated for ${stackName}`, {
        id: `resume-${stackName}`,
        description: 'Resources are starting up. Databases may take 2-5 minutes.',
      });
      queryClient.invalidateQueries({ queryKey: ['venture-stacks', venture] });
    },
    onError: (err: any, stackName) => {
      toast.error(`Failed to resume ${stackName}`, {
        id: `resume-${stackName}`,
        description: err?.response?.data?.error || err?.message || 'An unexpected error occurred.',
      });
    },
  });

  if (!VENTURES.includes(venture)) {
    return (
      <div className="text-center py-20 text-slate-500">
        Invalid venture. Choose from: {VENTURES.join(', ')}
      </div>
    );
  }

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><div className="spinner" /></div>;
  }

  if (error) {
    return <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center text-red-600">Failed to load data</div>;
  }

  const costs = data?.costs || [];
  const forecast = data?.forecast;
  const rawStacks: VentureStack[] = stacksData?.stacks || [];

  // Split into actionable stacks (have cost or hibernatable resources) vs serverless-only
  const actionableStacks = rawStacks.filter(
    s => s.costPerDay > 0.01 || s.costPerMonth > 0.01 || s.resources.some(r => r.canHibernate)
  );
  const serverlessStacks = rawStacks.filter(
    s => s.costPerDay <= 0.01 && s.costPerMonth <= 0.01 && !s.resources.some(r => r.canHibernate)
  );

  const dailyData = costs.map((c: any) => ({
    date: c.date,
    total: c.total,
  }));

  const serviceMap = new Map<string, number>();
  for (const day of costs) {
    for (const svc of day.services) {
      serviceMap.set(svc.service, (serviceMap.get(svc.service) || 0) + svc.amount);
    }
  }
  const serviceData = [...serviceMap.entries()]
    .map(([service, amount]) => ({ service, amount }))
    .sort((a, b) => b.amount - a.amount);

  const totalSpend = costs.reduce((s: number, c: any) => s + c.total, 0);

  return (
    <div className="space-y-8 fade-in">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/')} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
          <ArrowLeft className="w-5 h-5 text-slate-500" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{VENTURE_LABELS[venture]}</h1>
          <p className="text-slate-500">Last 30 days cost breakdown</p>
        </div>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="text-sm text-slate-500 mb-1">30-Day Total</div>
          <div className="text-2xl font-bold text-slate-900">{formatCurrency(totalSpend)}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="text-sm text-slate-500 mb-1">Daily Average</div>
          <div className="text-2xl font-bold text-slate-900">{formatCurrency(totalSpend / Math.max(costs.length, 1))}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="text-sm text-slate-500 mb-1">Forecast (EOM)</div>
          <div className="text-2xl font-bold text-slate-900">{forecast ? formatCurrency(forecast.forecastedTotal) : 'N/A'}</div>
        </div>
      </div>

      {/* Daily spend chart */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Daily Spend</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v.toFixed(0)}`} />
              <Tooltip formatter={(value) => formatCurrency(Number(value))} />
              <Area type="monotone" dataKey="total" stroke="#3b82f6" fill="#3b82f620" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Service breakdown */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Cost by Service</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={serviceData.slice(0, 10)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v.toFixed(0)}`} />
              <YAxis type="category" dataKey="service" tick={{ fontSize: 11 }} width={180} />
              <Tooltip formatter={(value) => formatCurrency(Number(value))} />
              <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
                {serviceData.slice(0, 10).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Stacks section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-slate-900 text-lg">Stacks</h3>
            {actionableStacks.some(s => s.status === 'transitioning') && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-medium">
                <Loader2 className="w-3 h-3 animate-spin" />
                Updating...
              </span>
            )}
          </div>
          {stacksLoading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
        </div>
        <HibernationInfo />
        {stacksError && (
          <div className="flex items-center gap-2 p-4 mb-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" />
            Failed to load stack information. Cost data may still be available above.
          </div>
        )}
        {actionableStacks.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {actionableStacks.map((stack) => (
              <StackCard
                key={stack.stackName}
                stack={stack}
                onHibernate={(name) => hibernateMutation.mutate(name)}
                onResume={(name) => resumeMutation.mutate(name)}
                isActioning={hibernateMutation.isPending || resumeMutation.isPending}
              />
            ))}
          </div>
        ) : !stacksLoading ? (
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 text-center text-sm space-y-2">
            <p className="text-slate-600 font-medium">All stacks are serverless — auto-scales to zero. No hibernation needed.</p>
            <p className="text-slate-400">Some costs shown above (EC2, RDS, VPC) may be attributed from shared infrastructure across ventures.</p>
          </div>
        ) : null}
        {serverlessStacks.length > 0 && actionableStacks.length > 0 && (
          <p className="mt-3 text-xs text-slate-400">
            {serverlessStacks.length} additional serverless {serverlessStacks.length === 1 ? 'stack' : 'stacks'} ({serverlessStacks.map(s => s.stackName).join(', ')}) auto-scale to zero — no action needed.
          </p>
        )}
      </div>

      {/* Venture navigation */}
      <div className="flex gap-3">
        {VENTURES.filter(v => v !== venture).map(v => (
          <button
            key={v}
            onClick={() => navigate(`/ventures/${v}`)}
            className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors"
          >
            View {VENTURE_LABELS[v]}
          </button>
        ))}
      </div>
    </div>
  );
}
